import React, { useMemo } from "react"
import { DimensionValue, StyleSheet, View, ViewProps } from "react-native"

import {
  BackgroundColor,
  backgroundColorToString,
} from "../lib/background-color"
import { BorderColor, borderColorToString } from "../lib/border-color"
import { BorderRadius, borderRadiusToNumber } from "../lib/border-radius"
import { Spacing, spacingToNumber } from "../lib/spacing"
import { useTokens } from "./tokens-provider"

export type BoxBorderColor = BorderColor
export type BoxBackgroundColor = BackgroundColor
export type BoxPadding = Spacing
export type BoxBorderRadius = BorderRadius

export type BoxProps = ViewProps & {
  position?: "absolute" | "relative" | "static"
  overflow?: "hidden" | "visible"
  backgroundColor?: BoxBackgroundColor
  width?: DimensionValue
  height?: DimensionValue
  minWidth?: DimensionValue
  minHeight?: DimensionValue
  maxWidth?: DimensionValue
  maxHeight?: DimensionValue
  flex?: number
  flexDirection?: "row" | "column"
  flexWrap?: "nowrap" | "wrap" | "wrap-reverse"
  alignItems?: "flex-start" | "flex-end" | "center" | "baseline" | "stretch"
  justifyContent?:
    | "center"
    | "flex-end"
    | "flex-start"
    | "space-around"
    | "space-between"
    | "space-evenly"
  gap?: Spacing | number
  rowGap?: Spacing | number
  columnGap?: Spacing | number
  borderRadius?: BoxBorderRadius
  borderColor?: BoxBorderColor
  borderTopColor?: BoxBorderColor
  borderRightColor?: BoxBorderColor
  borderBottomColor?: BoxBorderColor
  borderLeftColor?: BoxBorderColor
  padding?: BoxPadding
  paddingHorizontal?: BoxPadding
  paddingVertical?: BoxPadding
  paddingTop?: BoxPadding
  paddingBottom?: BoxPadding
  paddingLeft?: BoxPadding
  paddingRight?: BoxPadding
}

function Box({
  position,
  overflow,
  backgroundColor,
  width,
  height,
  minWidth,
  minHeight,
  maxWidth,
  maxHeight,
  flex,
  flexDirection,
  flexWrap,
  alignItems,
  justifyContent,
  gap,
  rowGap,
  columnGap,
  borderRadius,
  borderColor,
  borderTopColor,
  borderRightColor,
  borderBottomColor,
  borderLeftColor,
  padding,
  paddingHorizontal,
  paddingVertical,
  paddingTop,
  paddingBottom,
  paddingLeft,
  paddingRight,
  ...viewProps
}: BoxProps): React.JSX.Element {
  const { tokens } = useTokens()

  const style = useMemo(
    () =>
      StyleSheet.create({
        box: Object.fromEntries(
          Object.entries({
            position,
            overflow,
            width,
            height,
            minWidth,
            minHeight,
            maxWidth,
            maxHeight,
            flex,
            flexDirection,
            flexWrap,
            alignItems,
            justifyContent,
            gap: spacingToNumber(tokens, gap),
            rowGap: spacingToNumber(tokens, rowGap),
            columnGap: spacingToNumber(tokens, columnGap),
            backgroundColor: backgroundColorToString(tokens, backgroundColor),
            borderRadius: borderRadiusToNumber(tokens, borderRadius),
            borderWidth: borderColor !== undefined ? 2 : undefined,
            borderColor: borderColorToString(tokens, borderColor),
            borderTopColor: borderColorToString(tokens, borderTopColor),
            borderTopWidth: borderTopColor !== undefined ? 1 : undefined,
            borderRightColor: borderColorToString(tokens, borderRightColor),
            borderRightWidth: borderRightColor !== undefined ? 1 : undefined,
            borderBottomColor: borderColorToString(tokens, borderBottomColor),
            borderBottomWidth: borderBottomColor !== undefined ? 1 : undefined,
            borderLeftColor: borderColorToString(tokens, borderLeftColor),
            borderLeftWidth: borderLeftColor !== undefined ? 1 : undefined,
            padding: spacingToNumber(tokens, padding),
            paddingHorizontal: spacingToNumber(tokens, paddingHorizontal),
            paddingVertical: spacingToNumber(tokens, paddingVertical),
            paddingTop: spacingToNumber(tokens, paddingTop),
            paddingBottom: spacingToNumber(tokens, paddingBottom),
            paddingLeft: spacingToNumber(tokens, paddingLeft),
            paddingRight: spacingToNumber(tokens, paddingRight),
          }).filter(([_key, value]) => value !== undefined && value !== ""),
        ),
      }).box,
    [
      tokens,
      position,
      overflow,
      backgroundColor,
      width,
      height,
      minWidth,
      minHeight,
      maxWidth,
      maxHeight,
      flex,
      flexDirection,
      flexWrap,
      alignItems,
      justifyContent,
      gap,
      rowGap,
      columnGap,
      borderRadius,
      borderColor,
      borderTopColor,
      borderRightColor,
      borderBottomColor,
      borderLeftColor,
      padding,
      paddingHorizontal,
      paddingVertical,
      paddingTop,
      paddingBottom,
      paddingLeft,
      paddingRight,
    ],
  )

  return (
    <View
      {...viewProps}
      style={viewProps.style ? [viewProps.style, style] : style}
    />
  )
}

export default Box
